/**
 * Helpers for building the Odoo-style `domain` JSON string that the NestJS backend forwards.
 *
 * The shape is exactly what unified-doc-admin v1's `services/firebase.js` /
 * `services/apiservice.js` built before sending to Odoo, with one change: we always send the
 * JSON-stringified array under the `domain` query param rather than embedding it in the
 * Odoo REST URL.
 *
 * Used by consultations / sessions / evaluations services to translate the v1 filter object
 * `{ state, selectedDate, dateFrom, dateTo, doctorId, consultationChannel, campusId, patientId,
 * evaluationFormId, name }` into a real ERP domain.
 */

export const CONSULTATION_CHANNEL_VALUES = {
  in_person: "In-Person consultation",
  online: "Virtual Consultation",
};

function pushDate(parts, field, { selectedDate, dateFrom, dateTo }) {
  if (selectedDate) {
    parts.push([field, ">=", `${selectedDate} 00:00:00`]);
    parts.push([field, "<=", `${selectedDate} 23:59:59`]);
    return;
  }
  if (dateFrom) parts.push([field, ">=", `${dateFrom} 00:00:00`]);
  if (dateTo) parts.push([field, "<=", `${dateTo} 23:59:59`]);
}

export function jsonDomain(parts) {
  return JSON.stringify(parts || []);
}

/**
 * Build a consultation.consultation (psychiatrist) or clinical.psychologist.session domain.
 * `doctorField` is `psychiatrist_id` for psychiatrist, `clinical_psychologist_id` for the rest.
 */
export function buildConsultationDomain(filters = {}, doctorField, opts = {}) {
  const parts = [];
  const dateField = opts.dateField || "date";
  pushDate(parts, dateField, filters);

  if (filters.state && filters.state !== "all") {
    parts.push(["state", "=", filters.state]);
  }
  if (filters.doctorId != null && filters.doctorId !== "") {
    parts.push([doctorField, "=", Number(filters.doctorId)]);
  }
  if (filters.consultationChannel && filters.consultationChannel !== "all") {
    const odooValue = CONSULTATION_CHANNEL_VALUES[filters.consultationChannel];
    if (odooValue) parts.push(["consultation_type", "=", odooValue]);
  }
  if (filters.campusId) {
    parts.push(["campus_id", "=", Number(filters.campusId)]);
  }
  if (filters.patientId != null && filters.patientId !== "") {
    parts.push(["patient_id", "=", Number(filters.patientId)]);
  }
  if (filters.name) {
    parts.push(["patient_id.name", "ilike", filters.name]);
  }
  return parts;
}

/** Build a psychiatrist.evaluation.form domain.
 *
 * Filters: date range, state, psychiatrist, follow-up type, patient name, exact ID. */
export function buildEvaluationFormDomain(filters = {}, opts = {}) {
  const parts = [];
  const dateField = opts.dateField || "date";
  pushDate(parts, dateField, filters);

  if (filters.state && filters.state !== "all") {
    parts.push(["state", "=", filters.state]);
  }
  if (filters.doctorId != null && filters.doctorId !== "") {
    // Backwards-compat: legacy single-doctor filter applies to psychiatrist_id.
    parts.push(["psychiatrist_id", "=", Number(filters.doctorId)]);
  }
  if (filters.psychiatristId != null && filters.psychiatristId !== "") {
    parts.push(["psychiatrist_id", "=", Number(filters.psychiatristId)]);
  }
  if (filters.followupTypeId != null && filters.followupTypeId !== "") {
    parts.push(["followup_type_id", "=", Number(filters.followupTypeId)]);
  }
  if (filters.name) {
    parts.push(["patient_id.name", "ilike", filters.name]);
  }
  if (filters.evaluationFormId) {
    const idNum = parseInt(String(filters.evaluationFormId).trim(), 10);
    if (Number.isFinite(idNum)) parts.push(["id", "=", idNum]);
  }
  return parts;
}
